import React, { useState, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { searchDiagnosisCodesRouteDiagnosisCodesSearchGet } from '../../services/api/sdk.gen';
import { useDebounce } from '../../hooks/useDebounce';
import type { DiagnosisCode } from '../../types/codingData.types';

interface InlineDiagnosisSearchProps {
  description?: string;
  onSelect: (code: string, description: string) => void;
  isGhost?: boolean;
  placeholder?: string;
  disabled?: boolean;
  excludeCodes?: string[];
}

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query || query.length < 2) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className='font-bold'>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

const InlineDiagnosisSearch: React.FC<InlineDiagnosisSearchProps> = ({
  description,
  onSelect,
  isGhost = false,
  placeholder = 'Search diagnosis code...',
  disabled = false,
  excludeCodes,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<DiagnosisCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const excludeCodesRef = useRef(excludeCodes);
  excludeCodesRef.current = excludeCodes;

  const displayValue = isEditing ? searchTerm : description || '';

  const performSearch = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await searchDiagnosisCodesRouteDiagnosisCodesSearchGet({
        query: { search_text: term },
      });
      const data = (response.data as DiagnosisCode[]) || [];
      const codes = excludeCodesRef.current;
      setResults(codes ? data.filter(d => !codes.includes(d.name)) : data);
    } catch (error) {
      console.error('Error searching diagnosis codes:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useDebounce(performSearch, 300);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsEditing(true);

    if (value.length >= 2) {
      setOpen(true);
      setIsLoading(true);
      debouncedSearch(value);
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
    setSearchTerm(description || '');
    if ((description || '').length >= 2) {
      setOpen(true);
      debouncedSearch(description || '');
    }
  };

  const handleSelect = (dx: DiagnosisCode) => {
    onSelect(dx.name, dx.desc);
    setSearchTerm('');
    setResults([]);
    setOpen(false);
    setIsEditing(false);
    inputRef.current?.blur();
  };

  const handleBlur = (e: React.FocusEvent) => {
    // If focus moved to the popover content, don't close
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('[data-radix-popper-content-wrapper]')) return;
    setIsEditing(false);
    setSearchTerm('');
    setOpen(false);
  };

  const showDropdown = open && isEditing && searchTerm.length >= 2;

  return (
    <Popover open={showDropdown} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`relative flex-1 min-w-0 ${isGhost ? 'opacity-50' : ''}`}
        >
          <Search className='absolute left-2 top-1/2 w-3.5 -translate-y-1/2 text-muted-foreground z-10' />
          <Input
            ref={inputRef}
            type='text'
            placeholder={placeholder}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className='pl-7 pr-7 text-sm'
            disabled={disabled}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className='p-0 w-[var(--radix-popover-trigger-width)]'
        align='start'
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {isLoading ? (
          <div className='px-3 py-4 text-center text-sm text-muted-foreground'>
            Searching...
          </div>
        ) : results.length > 0 ? (
          <ul className='max-h-[250px] overflow-auto py-1'>
            {results.map((dx, i) => (
              <li key={`${dx.name}-${i}`}>
                <button
                  type='button'
                  className='w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none'
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleSelect(dx)}
                >
                  <div className='flex items-start gap-2'>
                    <span className='font-mono text-xs font-semibold text-primary mt-0.5 shrink-0'>
                      {highlightMatch(dx.name, searchTerm)}
                    </span>
                    <span className='text-sm truncate'>
                      {highlightMatch(dx.desc, searchTerm)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className='px-3 py-4 text-center text-sm text-muted-foreground'>
            No diagnosis codes found for &quot;{searchTerm}&quot;
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default InlineDiagnosisSearch;
